import React, { useState, useMemo, useCallback, useRef } from 'react';
import ApiService from '../../../_services/ApiService';

interface ActivitySlot {
  id: number;
  date: string;
  startTime: string;
  endTime: string;
  instructorId: number;
  instructorName: string;
  topicMasterId: number;
  topicName: string;
  agenda: string;
  batchIds?: number[];
  attendanceTaken?: boolean;
  roomId?: number;
  roomName?: string;
  centreId?: number;
  centreName?: string;
}

interface BatchMapping {
  batchId: number;
  batchName: string;
  batchCode?: string;
}

interface TemplateWeekViewProps {
  slots: ActivitySlot[];
  weekStartDate: string; // Monday YYYY-MM-DD
  templateStartDate: string;
  templateEndDate: string;
  templateId?: number;
  isEditMode: boolean;
  onWeekChange: (weekStart: string) => void;
  onDayClick: (date: string) => void;
  onAddSlot: (date?: string) => void;
  onEditSlot: (slot: ActivitySlot) => void;
  onDeleteSlot: (slotId: number) => void;
  onSlotMoved: () => void;
  templateBatchIds?: number[];
  batchMappings?: BatchMapping[];
  onAttendance?: (batchIds: number[], templateSlotId: number, instructorId?: number, instructorName?: string) => void;
  onAttendanceNode?: (batchIds: number[], templateSlotId: number, instructorId?: number, instructorName?: string) => void;
}

const PIXELS_PER_30MIN = 36;
const SNAP_MINUTES = 15;

function parseLocalDate(str: string): Date {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function addDaysToStr(dateStr: string, days: number): string {
  const d = parseLocalDate(dateStr);
  d.setDate(d.getDate() + days);
  return toDateKey(d);
}

function timeToMinutes(time: string): number {
  const parts = time.split(':');
  return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
}

function minutesToTime(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatTimeShort(time: string): string {
  if (!time) return '';
  const parts = time.split(':');
  const h = parseInt(parts[0], 10);
  const m = parts[1];
  const ampm = h >= 12 ? 'p' : 'a';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === '00' ? `${h12}${ampm}` : `${h12}:${m}${ampm}`;
}

function formatTimeLabel(time: string): string {
  const parts = time.split(':');
  const h = parseInt(parts[0], 10);
  const m = parts[1];
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m} ${ampm}`;
}

const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const TemplateWeekView: React.FC<TemplateWeekViewProps> = ({
  slots,
  weekStartDate,
  templateStartDate,
  templateEndDate,
  templateId,
  isEditMode,
  onWeekChange,
  onDayClick,
  onAddSlot,
  onEditSlot,
  onDeleteSlot,
  onSlotMoved,
  templateBatchIds = [],
  batchMappings = [],
  onAttendance,
  onAttendanceNode,
}) => {
  const [draggedSlotId, setDraggedSlotId] = useState<number | null>(null);
  const [colorMode, setColorMode] = useState<'instructor' | 'topic'>('instructor');
  const columnRefs = useRef<(HTMLDivElement | null)[]>([]);

  const batchNameMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const b of batchMappings) {
      map.set(b.batchId, b.batchCode || b.batchName || `Batch ${b.batchId}`);
    }
    return map;
  }, [batchMappings]);

  const getBatchLabel = (slot: ActivitySlot): string => {
    const ids = (slot.batchIds && slot.batchIds.length > 0) ? slot.batchIds : templateBatchIds;
    return ids.map(id => batchNameMap.get(id) || `Batch ${id}`).join(', ');
  };

  // Generate 7 day dates for this week
  const weekDates = useMemo(() => {
    const dates: string[] = [];
    for (let i = 0; i < 7; i++) {
      dates.push(addDaysToStr(weekStartDate, i));
    }
    return dates;
  }, [weekStartDate]);

  // Compute time range across all visible slots
  const { dayStartMinutes, dayEndMinutes, timeLabels } = useMemo(() => {
    let startMin = 7 * 60;
    let endMin = 21 * 60;

    const weekSlots = slots.filter(s => weekDates.includes(s.date));
    for (const s of weekSlots) {
      if (s.startTime) {
        const sm = timeToMinutes(s.startTime);
        if (sm < startMin) startMin = Math.floor(sm / 60) * 60;
      }
      if (s.endTime) {
        const em = timeToMinutes(s.endTime);
        if (em > endMin) endMin = Math.ceil(em / 60) * 60;
      }
    }

    const labels: { minutes: number; label: string }[] = [];
    for (let m = startMin; m <= endMin; m += 30) {
      labels.push({ minutes: m, label: formatTimeLabel(minutesToTime(m)) });
    }

    return { dayStartMinutes: startMin, dayEndMinutes: endMin, timeLabels: labels };
  }, [slots, weekDates]);

  const totalHeight = ((dayEndMinutes - dayStartMinutes) / 30) * PIXELS_PER_30MIN;

  // Group slots by date
  const slotsByDate = useMemo(() => {
    const map = new Map<string, ActivitySlot[]>();
    for (const s of slots) {
      if (!weekDates.includes(s.date)) continue;
      const existing = map.get(s.date) || [];
      existing.push(s);
      map.set(s.date, existing);
    }
    return map;
  }, [slots, weekDates]);

  // Slot color
  const getSlotColor = useCallback((slot: ActivitySlot) => {
    if (colorMode === 'instructor') {
      const hue = slot.instructorId ? (slot.instructorId * 137) % 360 : 210;
      return { bg: `hsl(${hue}, 55%, 90%)`, border: `hsl(${hue}, 65%, 55%)` };
    } else {
      const hue = slot.topicMasterId ? (slot.topicMasterId * 97) % 360 : 150;
      return { bg: `hsl(${hue}, 55%, 90%)`, border: `hsl(${hue}, 65%, 55%)` };
    }
  }, [colorMode]);

  // Get initials for compact display
  const getInitials = (name: string): string => {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Navigation
  const tStart = parseLocalDate(templateStartDate);
  const tEnd = parseLocalDate(templateEndDate);

  const canGoPrev = parseLocalDate(weekStartDate) > tStart;
  const canGoNext = parseLocalDate(addDaysToStr(weekStartDate, 6)) < tEnd;

  const formatWeekRange = (): string => {
    const start = parseLocalDate(weekStartDate);
    const end = parseLocalDate(addDaysToStr(weekStartDate, 6));
    const startDay = start.getDate();
    const endDay = end.getDate();
    const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
    const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
    const year = end.getFullYear();

    if (startMonth === endMonth) {
      return `${startDay} - ${endDay} ${startMonth} ${year}`;
    }
    return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
  };

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, slot: ActivitySlot) => {
    if (!isEditMode) return;
    setDraggedSlotId(slot.id);
    e.dataTransfer.setData('application/json', JSON.stringify({
      slotId: slot.id,
      date: slot.date,
      startTime: slot.startTime,
      endTime: slot.endTime,
    }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!isEditMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetDate: string, colRef: HTMLDivElement | null) => {
    e.preventDefault();
    setDraggedSlotId(null);

    if (!isEditMode || !colRef || !templateId) return;

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (!data.slotId) return;

      const rect = colRef.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const rawMinutes = dayStartMinutes + (y / PIXELS_PER_30MIN) * 30;
      const newStartMinutes = Math.round(rawMinutes / SNAP_MINUTES) * SNAP_MINUTES;

      const oldDuration = timeToMinutes(data.endTime) - timeToMinutes(data.startTime);
      const newEndMinutes = newStartMinutes + oldDuration;

      const newStartTime = minutesToTime(newStartMinutes);
      const newEndTime = minutesToTime(newEndMinutes);

      // Don't update if same position
      if (newStartTime === data.startTime && targetDate === data.date) return;

      // Append :00 for seconds to match HH:mm:ss format
      await ApiService.updateActivitySlotTemplate(templateId, data.slotId, {
        date: targetDate,
        startTime: newStartTime + ':00',
        endTime: newEndTime + ':00',
      });

      onSlotMoved();
    } catch (err) {
      console.error('Failed to move slot:', err);
    }
  };

  const handleDragEnd = () => {
    setDraggedSlotId(null);
  };

  // Compute overlapping columns for each day
  const computeDayColumns = (daySlots: ActivitySlot[]) => {
    const sorted = [...daySlots].sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));
    const result: { slot: ActivitySlot; col: number; totalCols: number }[] = [];
    const active: { col: number; endMin: number }[] = [];

    for (const slot of sorted) {
      const startMin = timeToMinutes(slot.startTime);
      const endMin = timeToMinutes(slot.endTime);

      const stillActive = active.filter(a => a.endMin > startMin);
      const usedCols = new Set(stillActive.map(a => a.col));

      let col = 0;
      while (usedCols.has(col)) col++;

      stillActive.push({ col, endMin });
      result.push({ slot, col, totalCols: 0 });

      active.length = 0;
      active.push(...stillActive);
    }

    // Second pass for totalCols
    for (const entry of result) {
      const startMin = timeToMinutes(entry.slot.startTime);
      const endMin = timeToMinutes(entry.slot.endTime);
      let maxCol = entry.col;
      for (const other of result) {
        const os = timeToMinutes(other.slot.startTime);
        const oe = timeToMinutes(other.slot.endTime);
        if (os < endMin && oe > startMin) {
          maxCol = Math.max(maxCol, other.col);
        }
      }
      entry.totalCols = maxCol + 1;
    }

    return result;
  };

  const today = new Date();
  const todayKey = toDateKey(today);

  return (
    <div className="template-week-view">
      {/* Week header */}
      <div className="week-view-header">
        <button
          className="week-nav-btn"
          onClick={() => onWeekChange(addDaysToStr(weekStartDate, -7))}
          disabled={!canGoPrev}
        >
          &lsaquo; Prev
        </button>
        <span className="week-view-title">{formatWeekRange()}</span>
        <button
          className="week-nav-btn"
          onClick={() => onWeekChange(addDaysToStr(weekStartDate, 7))}
          disabled={!canGoNext}
        >
          Next &rsaquo;
        </button>

        <div className="week-color-toggle">
          <button
            className={`color-btn ${colorMode === 'instructor' ? 'active' : ''}`}
            onClick={() => setColorMode('instructor')}
          >
            By Instructor
          </button>
          <button
            className={`color-btn ${colorMode === 'topic' ? 'active' : ''}`}
            onClick={() => setColorMode('topic')}
          >
            By Topic
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="week-grid-scroll">
        <div className="week-grid-container" style={{ height: totalHeight + 40 }}>
          {/* Day column headers */}
          <div className="week-day-headers">
            <div className="week-time-label-spacer" />
            {weekDates.map((dateStr, i) => {
              const d = parseLocalDate(dateStr);
              const dayNum = d.getDate();
              const isToday = dateStr === todayKey;
              const daySlots = slotsByDate.get(dateStr) || [];
              const inRange = d >= tStart && d <= tEnd;

              return (
                <div
                  key={dateStr}
                  className={`week-day-header ${isToday ? 'today' : ''} ${!inRange ? 'outside-range' : ''}`}
                  onClick={() => inRange ? onDayClick(dateStr) : undefined}
                >
                  <span className="week-day-name">{WEEKDAY_NAMES[i]}</span>
                  <span className={`week-day-num ${isToday ? 'today-num' : ''}`}>{dayNum}</span>
                  {daySlots.length > 0 && (
                    <span className="week-day-count">{daySlots.length}</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Time labels + Day columns */}
          <div className="week-grid-body">
            {/* Time labels column */}
            <div className="week-time-labels">
              {timeLabels.map(({ minutes, label }) => (
                <div
                  key={minutes}
                  className="week-time-label"
                  style={{ top: ((minutes - dayStartMinutes) / 30) * PIXELS_PER_30MIN }}
                >
                  {label}
                </div>
              ))}
            </div>

            {/* Day columns */}
            {weekDates.map((dateStr, i) => {
              const d = parseLocalDate(dateStr);
              const inRange = d >= tStart && d <= tEnd;
              const daySlots = slotsByDate.get(dateStr) || [];
              const dayColumns = computeDayColumns(daySlots);
              const isToday = dateStr === todayKey;

              return (
                <div
                  key={dateStr}
                  className={`week-day-column ${!inRange ? 'outside-range' : ''} ${isToday ? 'today' : ''}`}
                  style={{ height: totalHeight }}
                  ref={el => { columnRefs.current[i] = el; }}
                  onDragOver={inRange ? handleDragOver : undefined}
                  onDrop={inRange ? (e) => handleDrop(e, dateStr, columnRefs.current[i]) : undefined}
                >
                  {/* Grid lines */}
                  {timeLabels.map(({ minutes }) => (
                    <div
                      key={minutes}
                      className={`week-grid-line ${minutes % 60 === 0 ? 'hour' : 'half'}`}
                      style={{ top: ((minutes - dayStartMinutes) / 30) * PIXELS_PER_30MIN }}
                    />
                  ))}

                  {/* Slot cards */}
                  {dayColumns.map(({ slot, col, totalCols }) => {
                    const startMin = timeToMinutes(slot.startTime);
                    const endMin = timeToMinutes(slot.endTime);
                    const top = ((startMin - dayStartMinutes) / 30) * PIXELS_PER_30MIN;
                    const height = Math.max(((endMin - startMin) / 30) * PIXELS_PER_30MIN, 20);
                    const isDragging = draggedSlotId === slot.id;
                    const widthPct = 100 / totalCols;
                    const leftPct = col * widthPct;
                    const color = getSlotColor(slot);

                    return (
                      <div
                        key={slot.id}
                        className={`week-slot-card ${isDragging ? 'dragging' : ''} ${slot.attendanceTaken ? 'attendance-done' : ''}`}
                        style={{
                          top,
                          height,
                          left: `${leftPct}%`,
                          width: `${widthPct - 2}%`,
                          backgroundColor: color.bg,
                          borderLeftColor: color.border,
                        }}
                        draggable={isEditMode && !slot.attendanceTaken}
                        onDragStart={(e) => handleDragStart(e, slot)}
                        onDragEnd={handleDragEnd}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isEditMode && !slot.attendanceTaken) {
                            onEditSlot(slot);
                          } else if (onAttendance && ((slot.batchIds && slot.batchIds.length > 0) || templateBatchIds.length > 0)) {
                            onAttendance(
                              (slot.batchIds && slot.batchIds.length > 0) ? slot.batchIds : templateBatchIds,
                              slot.id,
                              slot.instructorId,
                              slot.instructorName
                            );
                          }
                        }}
                        title={`${slot.startTime}-${slot.endTime} ${slot.instructorName || ''} ${slot.topicName || ''}${slot.roomName ? `\nRoom: ${slot.roomName}${slot.centreName ? ` (${slot.centreName})` : ''}` : ''}${batchMappings.length > 0 ? `\n${getBatchLabel(slot)}` : ''}${!isEditMode ? '\nClick to take attendance' : ''}`}
                      >
                        <div className="week-slot-time">
                          {formatTimeShort(slot.startTime)}-{formatTimeShort(slot.endTime)}
                        </div>
                        {height > 30 && (
                          <div className="week-slot-info">
                            {slot.instructorName && (
                              <span className="week-slot-initials">{getInitials(slot.instructorName)}</span>
                            )}
                            {slot.topicName && (
                              <span className="week-slot-topic">{slot.topicName.substring(0, 10)}</span>
                            )}
                          </div>
                        )}
                        {height > 50 && slot.roomName && (
                          <div className="week-slot-room">🏫 {slot.roomName}</div>
                        )}
                        {height > 70 && batchMappings.length > 0 && (
                          <div className="week-slot-batch">{getBatchLabel(slot)}</div>
                        )}
                        {onAttendance && ((slot.batchIds && slot.batchIds.length > 0) || templateBatchIds.length > 0) && (
                          <button
                            className="week-slot-attendance"
                            onClick={(e) => {
                              e.stopPropagation();
                              onAttendance(
                                (slot.batchIds && slot.batchIds.length > 0) ? slot.batchIds : templateBatchIds,
                                slot.id,
                                slot.instructorId,
                                slot.instructorName
                              );
                            }}
                            title="Take Attendance (Java)"
                          >
                            A
                          </button>
                        )}
                        {onAttendanceNode && ((slot.batchIds && slot.batchIds.length > 0) || templateBatchIds.length > 0) && (
                          <button
                            className="week-slot-attendance"
                            style={{ background: 'var(--brand-primary)', color: 'var(--text-on-brand)' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onAttendanceNode(
                                (slot.batchIds && slot.batchIds.length > 0) ? slot.batchIds : templateBatchIds,
                                slot.id,
                                slot.instructorId,
                                slot.instructorName
                              );
                            }}
                            title="Take Attendance (Node — beta)"
                          >
                            A2
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateWeekView;
